import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import moment from 'moment'
import type { Moment } from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { SERVICE_TIME_FORMAT } from '@/app/constants/date'
import { ReportType } from '@/app/constants/reportType'
import { getReportNumberByType } from '@/app/services/printout/reportTypeResolver'
import { OutputType } from '@/app/constants/outputType'
import { v4 as uuidv4 } from 'uuid'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePrintJobStore } from '@/modules/printouts/stores/printJobStore'

const usePrintService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { activeLanguage } = useLocalize()

  const getPdf = async ({
    companyId,
    reportType,
    drivers,
    startTime,
    endTime,
    data,
    showLoading = true,
  }: {
    companyId: string
    reportType: ReportType
    drivers: Array<string>
    startTime: Moment | string
    endTime: Moment | string
    data?: any
    showLoading?: boolean
  }): Promise<string> => {
    const stopLoading = showLoading ? startLoading() : () => {}
    const finishJob = usePrintJobStore.getState().beginJob()
    try {
      // Generate UUID for task tracking
      const id = uuidv4()

      const startTimeStr =
        typeof startTime === 'string'
          ? startTime
          : moment(startTime).format(SERVICE_TIME_FORMAT)
      const endTimeStr =
        typeof endTime === 'string'
          ? endTime
          : moment(endTime).format(SERVICE_TIME_FORMAT)

      // Use the legacy task-based printout API
      await axiosInstance.post(
        `print-api/${companyId}/printout/print`,
        {
          id,
          drivers: drivers.map((d) => (typeof d === 'string' ? parseInt(d, 10) : d)),
          vehicles: null,
          reportType: getReportNumberByType(reportType),
          startTime: startTimeStr,
          endTime: endTimeStr,
          outputType: OutputType.Pdf,
          data,
          language: activeLanguage?.code,
        },
      )

      usePrintJobStore.getState().trackTask(id)

      // Return the task ID for tracking
      return id
    } catch (err) {
      console.error('Error generating PDF:', err)
      throw err
    } finally {
      stopLoading()
      finishJob()
    }
  }

  return {
    getPdf,
  }
}

export default usePrintService

